/**
 * AI Service Layer
 * Provides abstraction for AI operations with multiple provider support
 * Includes cost tracking, rate limiting, and error handling
 *
 * SECURITY NOTE: Gemini API calls are proxied through Firebase Cloud Functions
 * to keep the API key secure on the server-side.
 */

import { httpsCallable } from './firebase/authReadyCallable';
import { functions } from '../config/firebase';
import { GEMINI_MODELS } from '../config/gemini-models';

// ============================================================================
// TYPES
// ============================================================================

export type AIProvider = 'gemini';

// Cloud Function response type
interface CallGeminiResponse {
  success: boolean;
  data: {
    content: string;
    usage: {
      promptTokens: number;
      completionTokens: number;
      totalTokens: number;
    };
    model: string;
    finishReason: string;
  };
}

export interface AIServiceConfig {
  provider: AIProvider;
  apiKey: string;
  model?: string;
  maxTokens?: number;
  temperature?: number;
  rateLimitPerMinute?: number;
}

export interface AIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface AICompletionRequest {
  messages: AIMessage[];
  maxTokens?: number;
  temperature?: number;
  stopSequences?: string[];
  model?: string; // Override the default model for this request
}

export interface AICompletionResponse {
  content: string;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  model: string;
  finishReason: string;
}

export interface AIUsageRecord {
  timestamp: Date;
  provider: AIProvider;
  model: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCost: number;
  feature: string;
  userId?: string;
  organizationId?: string;
}

export interface RateLimitState {
  requestCount: number;
  windowStart: number;
  blocked: boolean;
}

// ============================================================================
// COST ESTIMATION
// ============================================================================

/**
 * Estimate cost based on provider and model (per-token rates).
 * Real published GA pricing (2026-08). PRO and FLASH both map to
 * gemini-3.8-flash, so a single FLASH entry covers both tiers — a separate
 * PRO computed key would silently collide (same literal) and drop FLASH.
 */
const TOKEN_COSTS = {
  gemini: {
    // gemini-3.5-flash-lite: $0.30 in / $2.50 out per 1M
    [GEMINI_MODELS.LITE]: { input: 0.0003 / 1000, output: 0.0025 / 1000 },
    // gemini-3.8-flash ( == PRO ): $1.50 in / $7.50 out per 1M (rate from 2027-01-01; $0.75 / $3.75 until 2026-12-31)
    [GEMINI_MODELS.FLASH]: { input: 0.0015 / 1000, output: 0.0075 / 1000 },
  },
};

function estimateCost(
  provider: AIProvider,
  model: string,
  promptTokens: number,
  completionTokens: number
): number {
  const providerCosts = TOKEN_COSTS[provider];
  if (!providerCosts) {
    console.warn(`Unknown provider ${provider}, using default cost`);
    return (promptTokens + completionTokens) * 0.001; // Fallback: $1 per 1M tokens
  }

  const costs = providerCosts[model as keyof typeof providerCosts] as { input: number; output: number } | undefined;
  if (!costs) {
    console.warn(`Unknown model ${model} for provider ${provider}, using default cost`);
    return (promptTokens + completionTokens) * 0.001; // Fallback: $1 per 1M tokens
  }

  return promptTokens * costs.input + completionTokens * costs.output;
}

// ============================================================================
// RATE LIMITING
// ============================================================================

class RateLimiter {
  private state: RateLimitState = {
    requestCount: 0,
    windowStart: Date.now(),
    blocked: false,
  };

  constructor(private maxRequestsPerMinute: number) {}

  canMakeRequest(): boolean {
    const now = Date.now();
    const windowDuration = 60 * 1000; // 1 minute in milliseconds

    // Reset window if expired
    if (now - this.state.windowStart >= windowDuration) {
      this.state = {
        requestCount: 0,
        windowStart: now,
        blocked: false,
      };
    }

    // Check if blocked
    if (this.state.requestCount >= this.maxRequestsPerMinute) {
      this.state.blocked = true;
      return false;
    }

    return true;
  }

  recordRequest(): void {
    this.state.requestCount++;
  }

  getTimeUntilReset(): number {
    const now = Date.now();
    const windowDuration = 60 * 1000;
    const elapsed = now - this.state.windowStart;
    return Math.max(0, windowDuration - elapsed);
  }
}

// ============================================================================
// USAGE TRACKING
// ============================================================================

class UsageTracker {
  private records: AIUsageRecord[] = [];
  private storageKey = 'ai_usage_records';

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.records = parsed.map((r: any) => ({
          ...r,
          timestamp: new Date(r.timestamp),
        }));
      }
    } catch (error) {
      console.error('Error loading usage records:', error);
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.records));
    } catch (error) {
      console.error('Error saving usage records:', error);
    }
  }

  recordUsage(record: AIUsageRecord): void {
    this.records.push(record);
    this.saveToStorage();

    // Keep only last 1000 records
    if (this.records.length > 1000) {
      this.records = this.records.slice(-1000);
      this.saveToStorage();
    }
  }

  getUsageByDateRange(startDate: Date, endDate: Date): AIUsageRecord[] {
    return this.records.filter(
      (r) => r.timestamp >= startDate && r.timestamp <= endDate
    );
  }

  getTotalCostByDateRange(startDate: Date, endDate: Date): number {
    const records = this.getUsageByDateRange(startDate, endDate);
    return records.reduce((sum, r) => sum + r.estimatedCost, 0);
  }

  getUsageByFeature(feature: string): AIUsageRecord[] {
    return this.records.filter((r) => r.feature === feature);
  }

  clearOldRecords(daysToKeep: number = 30): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    this.records = this.records.filter((r) => r.timestamp >= cutoffDate);
    this.saveToStorage();
  }
}

// ============================================================================
// AI SERVICE CLASS
// ============================================================================

export class AIService {
  private config: AIServiceConfig;
  private rateLimiter: RateLimiter;
  private usageTracker: UsageTracker;

  constructor(config: AIServiceConfig) {
    this.config = {
      maxTokens: 2000,
      temperature: 0.7,
      rateLimitPerMinute: 20,
      ...config,
    };

    this.rateLimiter = new RateLimiter(this.config.rateLimitPerMinute!);
    this.usageTracker = new UsageTracker();
  }

  /**
   * Make a completion request with automatic provider routing
   */
  async complete(
    request: AICompletionRequest,
    feature: string = 'general',
    metadata?: { userId?: string; organizationId?: string }
  ): Promise<AICompletionResponse> {
    // Check rate limit
    if (!this.rateLimiter.canMakeRequest()) {
      const timeUntilReset = this.rateLimiter.getTimeUntilReset();
      throw new Error(
        `Rate limit exceeded. Please wait ${Math.ceil(timeUntilReset / 1000)} seconds.`
      );
    }

    // Record request
    this.rateLimiter.recordRequest();

    // Route to Gemini via Cloud Functions
    const response = await this.completeWithGemini(request);

    // Track usage
    const estimatedCost = estimateCost(
      this.config.provider,
      response.model,
      response.usage.promptTokens,
      response.usage.completionTokens
    );

    this.usageTracker.recordUsage({
      timestamp: new Date(),
      provider: this.config.provider,
      model: response.model,
      promptTokens: response.usage.promptTokens,
      completionTokens: response.usage.completionTokens,
      totalTokens: response.usage.totalTokens,
      estimatedCost,
      feature,
      userId: metadata?.userId,
      organizationId: metadata?.organizationId,
    });

    return response;
  }

  /**
   * Google Gemini completion implementation
   *
   * SECURITY: This method calls a Firebase Cloud Function which keeps the
   * API key secure on the server-side. The API key is never exposed to the client.
   */
  private async completeWithGemini(
    request: AICompletionRequest
  ): Promise<AICompletionResponse> {
    const model = request.model || this.config.model || GEMINI_MODELS.LITE;
    const maxTokens = request.maxTokens || this.config.maxTokens;
    const temperature = request.temperature ?? this.config.temperature;

    // Call the Cloud Function instead of direct API
    const callGemini = httpsCallable<
      {
        operation: string;
        request: {
          messages: AIMessage[];
          maxTokens?: number;
          temperature?: number;
          stopSequences?: string[];
          model?: string;
        };
        context?: {
          feature?: string;
        };
      },
      CallGeminiResponse
    >(functions, 'callGemini');

    try {
      const result = await callGemini({
        operation: 'completion',
        request: {
          messages: request.messages,
          maxTokens,
          temperature,
          stopSequences: request.stopSequences,
          model,
        },
      });

      if (!result.data.success) {
        throw new Error('Gemini API request failed');
      }

      const { data } = result.data;

      return {
        content: data.content,
        usage: {
          promptTokens: data.usage.promptTokens,
          completionTokens: data.usage.completionTokens,
          totalTokens: data.usage.totalTokens,
        },
        model: data.model,
        finishReason: data.finishReason,
      };
    } catch (error: any) {
      // Handle Firebase function errors
      if (error.code === 'functions/unauthenticated') {
        throw new Error('Please sign in to use AI features');
      }
      if (error.code === 'functions/resource-exhausted') {
        throw new Error('AI rate limit exceeded. Please wait a moment and try again.');
      }
      if (error.code === 'functions/failed-precondition') {
        throw new Error('AI service is not configured. Please contact support.');
      }
      // Re-throw with original message
      throw new Error(`Gemini API error: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Get usage statistics
   */
  getUsageStats(startDate: Date, endDate: Date) {
    const records = this.usageTracker.getUsageByDateRange(startDate, endDate);
    const totalCost = this.usageTracker.getTotalCostByDateRange(startDate, endDate);

    const byFeature = records.reduce((acc, record) => {
      if (!acc[record.feature]) {
        acc[record.feature] = { count: 0, cost: 0, tokens: 0 };
      }
      acc[record.feature].count++;
      acc[record.feature].cost += record.estimatedCost;
      acc[record.feature].tokens += record.totalTokens;
      return acc;
    }, {} as Record<string, { count: number; cost: number; tokens: number }>);

    return {
      totalRequests: records.length,
      totalCost,
      totalTokens: records.reduce((sum, r) => sum + r.totalTokens, 0),
      byFeature,
      records,
    };
  }

  /**
   * Clear old usage records
   */
  clearOldUsageRecords(daysToKeep: number = 30): void {
    this.usageTracker.clearOldRecords(daysToKeep);
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<AIServiceConfig>): void {
    this.config = { ...this.config, ...config };
    if (config.rateLimitPerMinute) {
      this.rateLimiter = new RateLimiter(config.rateLimitPerMinute);
    }
  }

  /**
   * Get current configuration (without API key)
   */
  getConfig(): Omit<AIServiceConfig, 'apiKey'> {
    const { apiKey, ...safeConfig } = this.config;
    return safeConfig;
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

let aiServiceInstance: AIService | null = null;

export function initializeAIService(config: AIServiceConfig): AIService {
  aiServiceInstance = new AIService(config);
  return aiServiceInstance;
}

export function getAIService(): AIService {
  if (!aiServiceInstance) {
    throw new Error(
      'AI Service not initialized. Call initializeAIService() first.'
    );
  }
  return aiServiceInstance;
}

// ============================================================================
// REINFORCEMENT LEARNING & FEEDBACK SYSTEM
// ============================================================================

export type FeedbackType = 'positive' | 'negative' | 'neutral';
export type FeedbackCategory =
  | 'scope_of_work'
  | 'email_subject'
  | 'email_body'
  | 'line_item_description'
  | 'quote_formatting'
  | 'pricing_suggestion'
  | 'autocomplete'
  | 'general';

export interface AIFeedback {
  feedbackId: string;
  timestamp: Date;
  feature: string;
  category: FeedbackCategory;
  feedbackType: FeedbackType;

  // Context
  prompt: string;
  aiResponse: string;

  // Feedback details
  userComment?: string;
  correctedVersion?: string;
  rating?: number; // 1-5 scale

  // Metadata
  userId: string;
  organizationId: string;
  quoteId?: string;
  customerId?: string;

  // Reinforcement learning metadata
  modelUsed: string;
  temperature: number;
  tokens: number;
}

export interface FeedbackStats {
  totalFeedback: number;
  positiveCount: number;
  negativeCount: number;
  neutralCount: number;
  averageRating: number;
  byCategory: Record<FeedbackCategory, {
    count: number;
    positive: number;
    negative: number;
    neutral: number;
  }>;
  recentFeedback: AIFeedback[];
}

class FeedbackManager {
  private storageKey = 'ai_feedback_records';
  private feedbackRecords: AIFeedback[] = [];

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.feedbackRecords = parsed.map((r: any) => ({
          ...r,
          timestamp: new Date(r.timestamp),
        }));
      }
    } catch (error) {
      console.error('Error loading feedback records:', error);
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.feedbackRecords));
    } catch (error) {
      console.error('Error saving feedback records:', error);
    }
  }

  /**
   * Record feedback from admin or user
   */
  recordFeedback(feedback: Omit<AIFeedback, 'feedbackId' | 'timestamp'>): string {
    const feedbackId = `fb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const record: AIFeedback = {
      ...feedback,
      feedbackId,
      timestamp: new Date(),
    };

    this.feedbackRecords.push(record);
    this.saveToStorage();

    // Keep only last 500 feedback records locally
    if (this.feedbackRecords.length > 500) {
      this.feedbackRecords = this.feedbackRecords.slice(-500);
      this.saveToStorage();
    }

    return feedbackId;
  }

  /**
   * Get feedback statistics
   */
  getStats(startDate?: Date, endDate?: Date): FeedbackStats {
    let records = this.feedbackRecords;

    if (startDate || endDate) {
      records = records.filter(r => {
        const recordDate = r.timestamp;
        if (startDate && recordDate < startDate) return false;
        if (endDate && recordDate > endDate) return false;
        return true;
      });
    }

    const totalFeedback = records.length;
    const positiveCount = records.filter(r => r.feedbackType === 'positive').length;
    const negativeCount = records.filter(r => r.feedbackType === 'negative').length;
    const neutralCount = records.filter(r => r.feedbackType === 'neutral').length;

    const ratingsWithValue = records.filter(r => r.rating !== undefined);
    const averageRating = ratingsWithValue.length > 0
      ? ratingsWithValue.reduce((sum, r) => sum + (r.rating || 0), 0) / ratingsWithValue.length
      : 0;

    // Calculate by category
    const categories: FeedbackCategory[] = [
      'scope_of_work',
      'email_subject',
      'email_body',
      'line_item_description',
      'quote_formatting',
      'pricing_suggestion',
      'autocomplete',
      'general',
    ];

    const byCategory: Record<string, any> = {};
    categories.forEach(cat => {
      const catRecords = records.filter(r => r.category === cat);
      byCategory[cat] = {
        count: catRecords.length,
        positive: catRecords.filter(r => r.feedbackType === 'positive').length,
        negative: catRecords.filter(r => r.feedbackType === 'negative').length,
        neutral: catRecords.filter(r => r.feedbackType === 'neutral').length,
      };
    });

    return {
      totalFeedback,
      positiveCount,
      negativeCount,
      neutralCount,
      averageRating,
      byCategory,
      recentFeedback: records.slice(-20).reverse(),
    };
  }

  /**
   * Get feedback for a specific feature
   */
  getFeedbackByFeature(feature: string): AIFeedback[] {
    return this.feedbackRecords.filter(r => r.feature === feature);
  }

  /**
   * Get feedback for a specific category
   */
  getFeedbackByCategory(category: FeedbackCategory): AIFeedback[] {
    return this.feedbackRecords.filter(r => r.category === category);
  }

  /**
   * Get training examples for fine-tuning
   * Returns pairs of (prompt, good_response) based on positive feedback
   */
  getTrainingExamples(category?: FeedbackCategory): Array<{ prompt: string; response: string }> {
    let records = this.feedbackRecords.filter(r => r.feedbackType === 'positive');

    if (category) {
      records = records.filter(r => r.category === category);
    }

    return records.map(r => ({
      prompt: r.prompt,
      response: r.correctedVersion || r.aiResponse,
    }));
  }

  /**
   * Get negative examples for learning what to avoid
   */
  getNegativeExamples(category?: FeedbackCategory): Array<{
    prompt: string;
    badResponse: string;
    correctedResponse?: string;
    feedback: string;
  }> {
    let records = this.feedbackRecords.filter(r => r.feedbackType === 'negative');

    if (category) {
      records = records.filter(r => r.category === category);
    }

    return records.map(r => ({
      prompt: r.prompt,
      badResponse: r.aiResponse,
      correctedResponse: r.correctedVersion,
      feedback: r.userComment || 'No specific feedback provided',
    }));
  }

  /**
   * Clear old feedback records
   */
  clearOldFeedback(daysToKeep: number = 90): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    this.feedbackRecords = this.feedbackRecords.filter(r => r.timestamp >= cutoffDate);
    this.saveToStorage();
  }

  /**
   * Export feedback for external analysis or fine-tuning
   */
  exportFeedback(category?: FeedbackCategory): string {
    let records = this.feedbackRecords;
    if (category) {
      records = records.filter(r => r.category === category);
    }
    return JSON.stringify(records, null, 2);
  }
}

// Singleton feedback manager
let feedbackManagerInstance: FeedbackManager | null = null;

export function getFeedbackManager(): FeedbackManager {
  if (!feedbackManagerInstance) {
    feedbackManagerInstance = new FeedbackManager();
  }
  return feedbackManagerInstance;
}

// ============================================================================
// ENHANCED AI SERVICE WITH FEEDBACK
// ============================================================================

/**
 * Enhanced complete function that includes feedback context
 */
export async function completeWithFeedback(
  request: AICompletionRequest,
  feature: string,
  category: FeedbackCategory,
  metadata?: { userId?: string; organizationId?: string; quoteId?: string }
): Promise<{ response: AICompletionResponse; feedbackId: string | null }> {
  const aiService = getAIService();
  const feedbackManager = getFeedbackManager();

  // Get relevant feedback to improve context
  const positiveExamples = feedbackManager.getTrainingExamples(category);
  const negativeExamples = feedbackManager.getNegativeExamples(category);

  // Enhance prompt with feedback context (if available)
  if (positiveExamples.length > 0 || negativeExamples.length > 0) {
    const feedbackContext = buildFeedbackContext(positiveExamples, negativeExamples);

    // Prepend feedback context to system message
    const systemMessageIndex = request.messages.findIndex(m => m.role === 'system');
    if (systemMessageIndex >= 0) {
      request.messages[systemMessageIndex].content += '\n\n' + feedbackContext;
    } else {
      request.messages.unshift({
        role: 'system',
        content: feedbackContext,
      });
    }
  }

  // Make the completion request
  const response = await aiService.complete(request, feature, metadata);

  // Store initial feedback record (neutral by default, admin can update later)
  const feedbackId = feedbackManager.recordFeedback({
    feature,
    category,
    feedbackType: 'neutral',
    prompt: request.messages[request.messages.length - 1].content,
    aiResponse: response.content,
    userId: metadata?.userId || '',
    organizationId: metadata?.organizationId || '',
    quoteId: metadata?.quoteId,
    modelUsed: response.model,
    temperature: request.temperature || 0.7,
    tokens: response.usage.totalTokens,
  });

  return { response, feedbackId };
}

/**
 * Build feedback context to include in prompts
 */
function buildFeedbackContext(
  positiveExamples: Array<{ prompt: string; response: string }>,
  negativeExamples: Array<{ prompt: string; badResponse: string; correctedResponse?: string; feedback: string }>
): string {
  let context = 'Previous user feedback on similar tasks:\n\n';

  if (positiveExamples.length > 0) {
    context += '✓ GOOD EXAMPLES (these were well-received):\n';
    positiveExamples.slice(0, 3).forEach((ex, i) => {
      context += `\nExample ${i + 1}:\nRequest: ${ex.prompt.slice(0, 200)}...\nGood response: ${ex.response.slice(0, 200)}...\n`;
    });
  }

  if (negativeExamples.length > 0) {
    context += '\n\n✗ AVOID THESE PATTERNS (these received negative feedback):\n';
    negativeExamples.slice(0, 2).forEach((ex, i) => {
      context += `\nExample ${i + 1}:\nRequest: ${ex.prompt.slice(0, 200)}...\nPoor response: ${ex.badResponse.slice(0, 200)}...\nFeedback: ${ex.feedback}\n`;
      if (ex.correctedResponse) {
        context += `Corrected version: ${ex.correctedResponse.slice(0, 200)}...\n`;
      }
    });
  }

  context += '\n\nPlease apply these learnings to provide the best possible response.\n';
  return context;
}

/**
 * Update feedback after admin/user review
 */
export function updateFeedback(
  feedbackId: string,
  updates: {
    feedbackType?: FeedbackType;
    rating?: number;
    userComment?: string;
    correctedVersion?: string;
  }
): void {
  const feedbackManager = getFeedbackManager();
  const feedback = feedbackManager['feedbackRecords'].find(f => f.feedbackId === feedbackId);

  if (feedback) {
    Object.assign(feedback, updates);
    feedbackManager['saveToStorage']();
  }
}
